import { Autocomplete, AutocompleteRenderInputParams, Box, CircularProgress, TextField } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium';
import React, { FocusEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { useGetCreativeCreditsAndExperiences } from '../../../../components/person/person';
import { SortableChips } from '../../../../components/SortableChipsComponents/SortableChips';
import { ChipOption } from '../../../../components/SortableChipsComponents/types';
import { useI18n } from '../../../../i18n/I18n';
import { getCreditKey } from '../../../creditKey';
import { coerceKeyCreditsList } from '../../../data/utils';
import type { KeyCreditOption, KeyCreditsLineItemRow } from '../../../types';
import {
    buildKeyCreditOptions,
    buildSelectedKeyCreditOptions,
    filterKeyCreditOptions,
    formatKeyCredit,
    hasVisibleKeyCreditOptions,
    isFocusWithinAutocomplete,
    keyCreditsDropdownPopperModifiers,
    shouldShowKeyCreditAddOption
} from '../../utils';
import AddKeyCreditModal from '../AddKeyCreditModal';
import KeyCreditsDropdownOption from '../dataDisplay/KeyCreditsDropdownOption';
import KeyCreditsAutocompletePaper, { KeyCreditsAutocompletePaperProps } from '../internal/KeyCreditsAutocompletePaper';

type KeyCreditsEditCellProps = GridRenderEditCellParams & {
    onOpenKeyCreditsAI?: (row: KeyCreditsLineItemRow) => void;
    isAIEnabled?: boolean;
    /** When false, existing chips can be removed/reordered but no new credits can be added (AI, dropdown, or free text). */
    allowAdd?: boolean;
};

export default function KeyCreditsEditCell(props: KeyCreditsEditCellProps) {
    const { id, value, field, row, onOpenKeyCreditsAI, isAIEnabled = false, allowAdd = true } = props;
    const apiRef = useGridApiContext();
    const lineItemRow = row as KeyCreditsLineItemRow;
    const t = useI18n();

    const personId = row?.itemReference?.type === 'Person' || row?.isPerson ? row?.itemReference?.id : undefined;
    const { creditsAndExperienceDetails, creditsAndExperienceDetailsLoading } = useGetCreativeCreditsAndExperiences(
        personId,
        !personId
    );

    const selectedCredits: CreativeCredit[] = useMemo(
        () => coerceKeyCreditsList(value as CreativeCredit[] | null),
        [value]
    );

    const showAddCreditsCta = allowAdd && isAIEnabled && Boolean(onOpenKeyCreditsAI);

    const personCredits = useMemo(
        () => creditsAndExperienceDetails?.person?.creativeCredits ?? [],
        [creditsAndExperienceDetails?.person?.creativeCredits]
    );

    const selectedValue: KeyCreditOption[] = useMemo(
        () => buildSelectedKeyCreditOptions(selectedCredits, personCredits),
        [selectedCredits, personCredits]
    );

    const options: KeyCreditOption[] = useMemo(() => buildKeyCreditOptions(personCredits), [personCredits]);

    const hasDropdownOptions = options.length > 0;
    const [inputValue, setInputValue] = useState('');
    const [listOpen, setListOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addModalProductionName, setAddModalProductionName] = useState('');
    const addModalOpenRef = useRef(false);
    const canShowDropdown = allowAdd && hasVisibleKeyCreditOptions(options, inputValue, selectedValue);
    const canShowAddOption = allowAdd && shouldShowKeyCreditAddOption(options, inputValue);
    const canOpenDropdownPanel = canShowDropdown || showAddCreditsCta || canShowAddOption;

    useEffect(() => {
        if (!creditsAndExperienceDetailsLoading && canOpenDropdownPanel) {
            setListOpen(true);
        } else if (!canOpenDropdownPanel) {
            setListOpen(false);
        }
    }, [creditsAndExperienceDetailsLoading, canOpenDropdownPanel]);

    const commit = useCallback(
        (next: CreativeCredit[]) => {
            void apiRef.current.setEditCellValue({ id, field, value: next });
        },
        [apiRef, id, field]
    );

    const handleChange = useCallback(
        (_event: unknown, next: KeyCreditOption[], reason?: string, details?: { option?: KeyCreditOption }) => {
            if (reason === 'selectOption' && details?.option?.isAddOption && details.option.inputValue) {
                addModalOpenRef.current = true;
                setAddModalProductionName(details.option.inputValue);
                setAddModalOpen(true);
                setInputValue('');
                setListOpen(false);
                return;
            }

            const dedup: CreativeCredit[] = [];
            const seen = new Set<string>();
            for (const opt of next) {
                if (!opt || opt.isAddOption || seen.has(opt.key)) continue;
                seen.add(opt.key);
                dedup.push(opt.credit);
            }
            commit(dedup);
        },
        [commit]
    );

    const handleAddKeyCredit = useCallback(
        (credit: CreativeCredit) => {
            const key = getCreditKey(credit);
            if (selectedCredits.some((existing) => getCreditKey(existing) === key)) {
                setInputValue('');
                return;
            }
            commit([...selectedCredits, credit]);
            setInputValue('');
        },
        [commit, selectedCredits]
    );

    const handleCloseAddModal = useCallback(() => {
        addModalOpenRef.current = false;
        setAddModalOpen(false);
        setAddModalProductionName('');
    }, []);

    const filterOptionsWithAdd = useCallback(
        (opts: KeyCreditOption[], params: Parameters<typeof filterKeyCreditOptions>[1]) => {
            const filtered = filterKeyCreditOptions(opts, params);
            if (!allowAdd) {
                return filtered;
            }
            const trimmed = params.inputValue.trim();
            if (trimmed && shouldShowKeyCreditAddOption(opts, params.inputValue)) {
                filtered.push({
                    key: `add:${trimmed}`,
                    label: t('shortlist.line.item.key.credits.add', { value: trimmed }),
                    inputValue: trimmed,
                    isAddOption: true,
                    credit: { showName: trimmed } as CreativeCredit
                });
            }
            return filtered;
        },
        [allowAdd, t]
    );

    const handleReorder = useCallback(
        (chips: ChipOption[]) => {
            const keyToCredit = new Map(selectedValue.map((opt) => [opt.key, opt.credit]));
            const next = chips
                .map((chip) => keyToCredit.get(chip.id ?? ''))
                .filter((c): c is CreativeCredit => Boolean(c));
            if (next.length === selectedValue.length) {
                commit(next);
            }
        },
        [selectedValue, commit]
    );

    const stopEditing = useCallback(() => {
        apiRef.current.stopCellEditMode({ id, field });
    }, [apiRef, id, field]);

    const handleInputBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            if (addModalOpenRef.current) return;

            const related = event.relatedTarget as Element | null;
            if (isFocusWithinAutocomplete(related)) return;

            window.setTimeout(() => {
                if (isFocusWithinAutocomplete(document.activeElement)) return;
                setListOpen(false);
                stopEditing();
            }, 0);
        },
        [stopEditing]
    );

    const handleOpenAiPanel = useCallback(
        (e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            apiRef.current.stopCellEditMode({ id, field });
            onOpenKeyCreditsAI?.(lineItemRow);
        },
        [apiRef, field, id, lineItemRow, onOpenKeyCreditsAI]
    );

    return (
        <Box sx={{ width: '100%', px: 0.5, py: 0.5 }}>
            <Autocomplete<KeyCreditOption, true, true, false>
                multiple
                autoFocus
                inputValue={inputValue}
                onInputChange={(_, value) => setInputValue(value)}
                open={listOpen && canOpenDropdownPanel}
                onOpen={() => canOpenDropdownPanel && setListOpen(true)}
                onClose={() => setListOpen(false)}
                openOnFocus={allowAdd && (hasDropdownOptions || showAddCreditsCta || canShowAddOption)}
                disableCloseOnSelect
                disableClearable
                filterSelectedOptions
                size="small"
                fullWidth
                loading={creditsAndExperienceDetailsLoading}
                options={options}
                value={selectedValue}
                onChange={handleChange}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(opt, val) => opt.key === val.key}
                filterOptions={filterOptionsWithAdd}
                renderOption={(optionProps, option) => (
                    <li {...optionProps} key={option.key}>
                        <KeyCreditsDropdownOption option={option} />
                    </li>
                )}
                renderTags={(tagValue, getTagProps) => {
                    const chips: ChipOption[] = tagValue.map((opt) => ({
                        id: opt.key,
                        label: formatKeyCredit(opt.credit) || opt.label
                    }));
                    return (
                        <SortableChips
                            chips={chips}
                            getTagProps={getTagProps}
                            fixedOptions={[]}
                            onReorder={handleReorder}
                            getChipKey={(chip) => chip.id ?? chip.label}
                        />
                    );
                }}
                slots={{
                    paper: KeyCreditsAutocompletePaper
                }}
                slotProps={{
                    popper: {
                        placement: 'bottom-start',
                        modifiers: keyCreditsDropdownPopperModifiers
                    },
                    paper: {
                        showAddCreditsCta,
                        emptyListArea: showAddCreditsCta && !canShowDropdown && !canShowAddOption,
                        onOpenAiPanel: handleOpenAiPanel,
                        sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            width: '100%'
                        }
                    } as KeyCreditsAutocompletePaperProps,
                    listbox: {
                        sx: showAddCreditsCta && !canShowDropdown && !canShowAddOption ? { display: 'none' } : undefined
                    }
                }}
                noOptionsText={null}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        autoFocus
                        variant="outlined"
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                readOnly: !allowAdd,
                                onBlur: (event: FocusEvent<HTMLInputElement>) => {
                                    handleInputBlur(event);
                                },
                                endAdornment: (
                                    <>
                                        {creditsAndExperienceDetailsLoading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={14}
                                                sx={{ flexShrink: 0, mr: 0.5 }}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }
                        }}
                    />
                )}
            />
            {allowAdd && addModalOpen ? (
                <AddKeyCreditModal
                    isOpen={addModalOpen}
                    initialProductionName={addModalProductionName}
                    onClose={handleCloseAddModal}
                    onAdd={handleAddKeyCredit}
                    lineItemRow={lineItemRow}
                />
            ) : null}
        </Box>
    );
}
